import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Plus } from "lucide-react";
import { useCompany } from "../../context/CompanyContext";
import {
  getEmployees,
  getBenefitPlans,
  createBenefitPlan,
  getEmployeeBenefits,
  assignBenefitToEmployee,
  getLoans,
  createLoan,
} from "../payrollService";

const initialBenefitPlanForm = {
  benefit_code: "",
  benefit_name: "",
  benefit_type: "health",
  employer_cost: "",
  employee_cost: "",
  taxable: false,
  active: true,
};

const initialAssignBenefitForm = {
  employee_id: "",
  benefit_plan_id: "",
  employee_cost_override: "",
  employer_cost_override: "",
  effective_from: "",
};

const initialLoanForm = {
  employee_id: "",
  loan_name: "",
  principal_amount: "",
  interest_rate: "",
  repayment_start: "",
  repayment_months: "",
  monthly_deduction: "",
  outstanding_balance: "",
  notes: "",
};

function pickOne(value) {
  if (!value) return null;
  return Array.isArray(value) ? value[0] || null : value;
}

export default function PayrollBenefits() {
  const { companyId } = useCompany();

  const [busy, setBusy] = useState(true);
  const [msg, setMsg] = useState("");

  const [employees, setEmployees] = useState([]);
  const [benefitPlans, setBenefitPlans] = useState([]);
  const [employeeBenefits, setEmployeeBenefits] = useState([]);
  const [loans, setLoans] = useState([]);

  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showLoanModal, setShowLoanModal] = useState(false);

  const [benefitPlanForm, setBenefitPlanForm] = useState(
    initialBenefitPlanForm
  );
  const [assignBenefitForm, setAssignBenefitForm] = useState(
    initialAssignBenefitForm
  );
  const [loanForm, setLoanForm] = useState(initialLoanForm);

  const load = useCallback(async () => {
    if (!companyId) return;

    setBusy(true);
    setMsg("");

    try {
      const [employeeData, planData, employeeBenefitData, loanData] =
        await Promise.all([
          getEmployees(companyId),
          getBenefitPlans(companyId),
          getEmployeeBenefits(companyId),
          getLoans(companyId),
        ]);

      setEmployees(employeeData || []);
      setBenefitPlans(planData || []);
      setEmployeeBenefits(employeeBenefitData || []);
      setLoans(loanData || []);
    } catch (err) {
      setMsg(err.message || "Failed to load benefits and loans");
    } finally {
      setBusy(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const activePlans = useMemo(
    () => benefitPlans.filter((x) => x.active).length,
    [benefitPlans]
  );

  const activeLoans = useMemo(
    () => loans.filter((x) => x.status === "active").length,
    [loans]
  );

  async function handleCreatePlan(e) {
    e.preventDefault();
    try {
      setMsg("");

      await createBenefitPlan({
        company_id: companyId,
        benefit_code: benefitPlanForm.benefit_code.trim().toUpperCase(),
        benefit_name: benefitPlanForm.benefit_name.trim(),
        benefit_type: benefitPlanForm.benefit_type,
        employer_cost: Number(benefitPlanForm.employer_cost || 0),
        employee_cost: Number(benefitPlanForm.employee_cost || 0),
        taxable: !!benefitPlanForm.taxable,
        active: !!benefitPlanForm.active,
      });

      setBenefitPlanForm(initialBenefitPlanForm);
      setShowPlanModal(false);
      await load();
      setMsg("Benefit plan created successfully.");
    } catch (err) {
      setMsg(err.message || "Failed to create benefit plan");
    }
  }

  async function handleAssignBenefit(e) {
    e.preventDefault();
    try {
      setMsg("");

      await assignBenefitToEmployee({
        company_id: companyId,
        employee_id: assignBenefitForm.employee_id,
        benefit_plan_id: assignBenefitForm.benefit_plan_id,
        employee_cost_override:
          assignBenefitForm.employee_cost_override === ""
            ? null
            : Number(assignBenefitForm.employee_cost_override),
        employer_cost_override:
          assignBenefitForm.employer_cost_override === ""
            ? null
            : Number(assignBenefitForm.employer_cost_override),
        effective_from:
          assignBenefitForm.effective_from ||
          new Date().toISOString().slice(0, 10),
        active: true,
      });

      setAssignBenefitForm(initialAssignBenefitForm);
      setShowAssignModal(false);
      await load();
      setMsg("Benefit assigned successfully.");
    } catch (err) {
      setMsg(err.message || "Failed to assign benefit");
    }
  }

  async function handleCreateLoan(e) {
    e.preventDefault();
    try {
      setMsg("");

      await createLoan({
        company_id: companyId,
        employee_id: loanForm.employee_id,
        loan_name: loanForm.loan_name.trim(),
        principal_amount: Number(loanForm.principal_amount || 0),
        interest_rate: Number(loanForm.interest_rate || 0),
        repayment_start: loanForm.repayment_start,
        repayment_months: Number(loanForm.repayment_months || 1),
        monthly_deduction: Number(loanForm.monthly_deduction || 0),
        outstanding_balance: Number(
          loanForm.outstanding_balance || loanForm.principal_amount || 0
        ),
        status: "active",
        notes: loanForm.notes,
      });

      setLoanForm(initialLoanForm);
      setShowLoanModal(false);
      await load();
      setMsg("Loan created successfully.");
    } catch (err) {
      setMsg(err.message || "Failed to create loan");
    }
  }

  return (
    <div className="payroll-page">
      {msg ? <div className="payroll-alert info">{msg}</div> : null}

      <div className="payroll-grid payroll-grid-4">
        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Benefit Plans</p>
          <h3>{benefitPlans.length}</h3>
          <span>Configured benefit structures</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Active Plans</p>
          <h3>{activePlans}</h3>
          <span>Currently active benefit plans</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Assignments</p>
          <h3>{employeeBenefits.length}</h3>
          <span>Employee benefit enrollments</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Active Loans</p>
          <h3>{activeLoans}</h3>
          <span>Employee loans in repayment</span>
        </div>
      </div>

      <div className="payroll-card">
        <div className="payroll-card-header">
          <div>
            <h3>Benefits & Loans</h3>
            <p>
              Manage benefit plans, assign benefits, and track employee loans.
            </p>
          </div>

          <div className="table-actions">
            <button
              type="button"
              className="ghost-btn"
              onClick={() => setShowPlanModal(true)}
            >
              <Plus size={16} />
              Add Benefit Plan
            </button>

            <button
              type="button"
              className="ghost-btn"
              onClick={() => setShowAssignModal(true)}
            >
              <Plus size={16} />
              Assign Benefit
            </button>

            <button
              type="button"
              className="primary-btn"
              onClick={() => setShowLoanModal(true)}
            >
              <Plus size={16} />
              Add Loan
            </button>
          </div>
        </div>

        <div className="payroll-grid payroll-grid-2">
          <div className="payroll-card">
            <div className="payroll-card-header">
              <h3>Benefit Plans</h3>
            </div>

            <div className="payroll-table-wrap">
              <table className="payroll-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Employer Cost</th>
                    <th>Employee Cost</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {benefitPlans.map((row) => (
                    <tr key={row.id}>
                      <td>{row.benefit_code}</td>
                      <td>{row.benefit_name}</td>
                      <td>{row.benefit_type}</td>
                      <td>{Number(row.employer_cost || 0).toLocaleString()}</td>
                      <td>{Number(row.employee_cost || 0).toLocaleString()}</td>
                      <td>
                        <span
                          className={`pill ${
                            row.active ? "active" : "inactive"
                          }`}
                        >
                          {row.active ? "active" : "inactive"}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {!benefitPlans.length && !busy && (
                    <tr>
                      <td colSpan="6">No benefit plans found.</td>
                    </tr>
                  )}
                  {busy && (
                    <tr>
                      <td colSpan="6">Loading...</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="payroll-card">
            <div className="payroll-card-header">
              <h3>Employee Benefit Assignments</h3>
            </div>

            <div className="payroll-table-wrap">
              <table className="payroll-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Benefit</th>
                    <th>Employee Override</th>
                    <th>Employer Override</th>
                    <th>Effective From</th>
                  </tr>
                </thead>
                <tbody>
                  {employeeBenefits.map((row) => {
                    const employee = pickOne(row.payroll_employees);
                    const benefit = pickOne(row.payroll_benefit_plans);

                    return (
                      <tr key={row.id}>
                        <td>
                          {employee
                            ? `${employee.employee_code} - ${employee.first_name} ${employee.last_name}`
                            : "-"}
                        </td>
                        <td>{benefit?.benefit_name || "-"}</td>
                        <td>
                          {row.employee_cost_override == null
                            ? "-"
                            : Number(
                                row.employee_cost_override
                              ).toLocaleString()}
                        </td>
                        <td>
                          {row.employer_cost_override == null
                            ? "-"
                            : Number(
                                row.employer_cost_override
                              ).toLocaleString()}
                        </td>
                        <td>{row.effective_from || "-"}</td>
                      </tr>
                    );
                  })}
                  {!employeeBenefits.length && !busy && (
                    <tr>
                      <td colSpan="5">No benefit assignments found.</td>
                    </tr>
                  )}
                  {busy && (
                    <tr>
                      <td colSpan="5">Loading...</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="payroll-card">
          <div className="payroll-card-header">
            <h3>Loans</h3>
          </div>

          <div className="payroll-table-wrap">
            <table className="payroll-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Loan Name</th>
                  <th>Principal</th>
                  <th>Monthly Deduction</th>
                  <th>Outstanding</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loans.map((row) => {
                  const employee = pickOne(row.payroll_employees);

                  return (
                    <tr key={row.id}>
                      <td>
                        {employee
                          ? `${employee.employee_code} - ${employee.first_name} ${employee.last_name}`
                          : "-"}
                      </td>
                      <td>{row.loan_name}</td>
                      <td>
                        {Number(row.principal_amount || 0).toLocaleString()}
                      </td>
                      <td>
                        {Number(row.monthly_deduction || 0).toLocaleString()}
                      </td>
                      <td>
                        {Number(row.outstanding_balance || 0).toLocaleString()}
                      </td>
                      <td>
                        <span className={`pill ${row.status}`}>
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {!loans.length && !busy && (
                  <tr>
                    <td colSpan="6">No loans found.</td>
                  </tr>
                )}
                {busy && (
                  <tr>
                    <td colSpan="6">Loading...</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showPlanModal ? (
        <div className="payroll-modal-overlay">
          <div className="payroll-modal">
            <div className="payroll-modal-header">
              <h3>Create Benefit Plan</h3>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => setShowPlanModal(false)}
              >
                Close
              </button>
            </div>

            <form className="payroll-form" onSubmit={handleCreatePlan}>
              <div className="payroll-grid payroll-grid-2">
                <label>
                  Benefit Code
                  <input
                    value={benefitPlanForm.benefit_code}
                    onChange={(e) =>
                      setBenefitPlanForm((p) => ({
                        ...p,
                        benefit_code: e.target.value,
                      }))
                    }
                    required
                  />
                </label>

                <label>
                  Benefit Name
                  <input
                    value={benefitPlanForm.benefit_name}
                    onChange={(e) =>
                      setBenefitPlanForm((p) => ({
                        ...p,
                        benefit_name: e.target.value,
                      }))
                    }
                    required
                  />
                </label>

                <label>
                  Benefit Type
                  <select
                    value={benefitPlanForm.benefit_type}
                    onChange={(e) =>
                      setBenefitPlanForm((p) => ({
                        ...p,
                        benefit_type: e.target.value,
                      }))
                    }
                  >
                    <option value="health">Health</option>
                    <option value="life">Life</option>
                    <option value="transport">Transport</option>
                    <option value="meal">Meal</option>
                    <option value="housing">Housing</option>
                    <option value="other">Other</option>
                  </select>
                </label>

                <label>
                  Employer Cost
                  <input
                    type="number"
                    step="0.01"
                    value={benefitPlanForm.employer_cost}
                    onChange={(e) =>
                      setBenefitPlanForm((p) => ({
                        ...p,
                        employer_cost: e.target.value,
                      }))
                    }
                  />
                </label>

                <label>
                  Employee Cost
                  <input
                    type="number"
                    step="0.01"
                    value={benefitPlanForm.employee_cost}
                    onChange={(e) =>
                      setBenefitPlanForm((p) => ({
                        ...p,
                        employee_cost: e.target.value,
                      }))
                    }
                  />
                </label>

                <label className="check-inline">
                  <input
                    type="checkbox"
                    checked={benefitPlanForm.taxable}
                    onChange={(e) =>
                      setBenefitPlanForm((p) => ({
                        ...p,
                        taxable: e.target.checked,
                      }))
                    }
                  />
                  Taxable Benefit
                </label>

                <label className="check-inline">
                  <input
                    type="checkbox"
                    checked={benefitPlanForm.active}
                    onChange={(e) =>
                      setBenefitPlanForm((p) => ({
                        ...p,
                        active: e.target.checked,
                      }))
                    }
                  />
                  Active
                </label>
              </div>

              <div className="payroll-form-actions">
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() => setShowPlanModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-btn">
                  Save Benefit Plan
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {showAssignModal ? (
        <div className="payroll-modal-overlay">
          <div className="payroll-modal">
            <div className="payroll-modal-header">
              <h3>Assign Benefit To Employee</h3>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => setShowAssignModal(false)}
              >
                Close
              </button>
            </div>

            <form className="payroll-form" onSubmit={handleAssignBenefit}>
              <div className="payroll-grid payroll-grid-2">
                <label>
                  Employee
                  <select
                    value={assignBenefitForm.employee_id}
                    onChange={(e) =>
                      setAssignBenefitForm((p) => ({
                        ...p,
                        employee_id: e.target.value,
                      }))
                    }
                    required
                  >
                    <option value="">Select employee</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.employee_code} - {emp.first_name} {emp.last_name}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Benefit Plan
                  <select
                    value={assignBenefitForm.benefit_plan_id}
                    onChange={(e) =>
                      setAssignBenefitForm((p) => ({
                        ...p,
                        benefit_plan_id: e.target.value,
                      }))
                    }
                    required
                  >
                    <option value="">Select benefit plan</option>
                    {benefitPlans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.benefit_name}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Employee Cost Override
                  <input
                    type="number"
                    step="0.01"
                    value={assignBenefitForm.employee_cost_override}
                    onChange={(e) =>
                      setAssignBenefitForm((p) => ({
                        ...p,
                        employee_cost_override: e.target.value,
                      }))
                    }
                  />
                </label>

                <label>
                  Employer Cost Override
                  <input
                    type="number"
                    step="0.01"
                    value={assignBenefitForm.employer_cost_override}
                    onChange={(e) =>
                      setAssignBenefitForm((p) => ({
                        ...p,
                        employer_cost_override: e.target.value,
                      }))
                    }
                  />
                </label>

                <label>
                  Effective From
                  <input
                    type="date"
                    value={assignBenefitForm.effective_from}
                    onChange={(e) =>
                      setAssignBenefitForm((p) => ({
                        ...p,
                        effective_from: e.target.value,
                      }))
                    }
                  />
                </label>
              </div>

              <div className="payroll-form-actions">
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() => setShowAssignModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-btn">
                  Save Assignment
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {showLoanModal ? (
        <div className="payroll-modal-overlay">
          <div className="payroll-modal">
            <div className="payroll-modal-header">
              <h3>Create Employee Loan</h3>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => setShowLoanModal(false)}
              >
                Close
              </button>
            </div>

            <form className="payroll-form" onSubmit={handleCreateLoan}>
              <div className="payroll-grid payroll-grid-2">
                <label>
                  Employee
                  <select
                    value={loanForm.employee_id}
                    onChange={(e) =>
                      setLoanForm((p) => ({
                        ...p,
                        employee_id: e.target.value,
                      }))
                    }
                    required
                  >
                    <option value="">Select employee</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.employee_code} - {emp.first_name} {emp.last_name}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Loan Name
                  <input
                    value={loanForm.loan_name}
                    onChange={(e) =>
                      setLoanForm((p) => ({ ...p, loan_name: e.target.value }))
                    }
                    required
                  />
                </label>

                <label>
                  Principal Amount
                  <input
                    type="number"
                    step="0.01"
                    value={loanForm.principal_amount}
                    onChange={(e) =>
                      setLoanForm((p) => ({
                        ...p,
                        principal_amount: e.target.value,
                      }))
                    }
                    required
                  />
                </label>

                <label>
                  Interest Rate (%)
                  <input
                    type="number"
                    step="0.0001"
                    value={loanForm.interest_rate}
                    onChange={(e) =>
                      setLoanForm((p) => ({
                        ...p,
                        interest_rate: e.target.value,
                      }))
                    }
                  />
                </label>

                <label>
                  Repayment Start
                  <input
                    type="date"
                    value={loanForm.repayment_start}
                    onChange={(e) =>
                      setLoanForm((p) => ({
                        ...p,
                        repayment_start: e.target.value,
                      }))
                    }
                    required
                  />
                </label>

                <label>
                  Repayment Months
                  <input
                    type="number"
                    value={loanForm.repayment_months}
                    onChange={(e) =>
                      setLoanForm((p) => ({
                        ...p,
                        repayment_months: e.target.value,
                      }))
                    }
                    required
                  />
                </label>

                <label>
                  Monthly Deduction
                  <input
                    type="number"
                    step="0.01"
                    value={loanForm.monthly_deduction}
                    onChange={(e) =>
                      setLoanForm((p) => ({
                        ...p,
                        monthly_deduction: e.target.value,
                      }))
                    }
                    required
                  />
                </label>

                <label>
                  Outstanding Balance
                  <input
                    type="number"
                    step="0.01"
                    value={loanForm.outstanding_balance}
                    onChange={(e) =>
                      setLoanForm((p) => ({
                        ...p,
                        outstanding_balance: e.target.value,
                      }))
                    }
                  />
                </label>
              </div>

              <label>
                Notes
                <textarea
                  rows="4"
                  value={loanForm.notes}
                  onChange={(e) =>
                    setLoanForm((p) => ({ ...p, notes: e.target.value }))
                  }
                />
              </label>

              <div className="payroll-form-actions">
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() => setShowLoanModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-btn">
                  Save Loan
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
